import type { z } from 'zod';
import { type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBLessonEnrolmentSchema, LessonEnrolmentSchema } from './schemas';

type DBLessonEnrolment = z.infer<typeof DBLessonEnrolmentSchema>;
type LessonEnrolment = z.infer<typeof LessonEnrolmentSchema>;

export const LessonEnrolmentCodecConfig: TransformerConfig<
  typeof LessonEnrolmentSchema,
  typeof DBLessonEnrolmentSchema
> = {
  name: 'LessonEnrolment',
  clientSchema: LessonEnrolmentSchema,
  dbSchema: DBLessonEnrolmentSchema,
  transformers: [
    // classId + lesson ↔ classLessonIdentifier composite key
    createTransformer<typeof LessonEnrolmentSchema, typeof DBLessonEnrolmentSchema>({
      encoder: {
        sourceFields: ['classId', 'lesson'] as const,
        encode: ({ classId, lesson }) => ({
          classLessonIdentifier: `${(classId as LessonEnrolment['classId']).subject}#${(classId as LessonEnrolment['classId']).classId}#${(lesson as LessonEnrolment['lesson']).course}#${(lesson as LessonEnrolment['lesson']).lessonId}#${(lesson as LessonEnrolment['lesson']).counter}`,
        }),
      },
      decoder: {
        sourceFields: ['classLessonIdentifier'] as const,
        decode: ({ classLessonIdentifier }) => {
          const parts = (classLessonIdentifier as DBLessonEnrolment['classLessonIdentifier']).split(
            '#'
          );
          return {
            classId: {
              subject: parts[0] as Subject,
              classId: parts[1] as string,
            },
            lesson: {
              course: parts[2] as string,
              lessonId: parts[3] as string,
              counter: parts[4] as string,
            },
          };
        },
      },
    }),

    // rollMarked: boolean ↔ rollMarked: 'true' | 'false'
    createTransformer<typeof LessonEnrolmentSchema, typeof DBLessonEnrolmentSchema>({
      encoder: {
        sourceFields: ['rollMarked'] as const,
        encode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as LessonEnrolment['rollMarked']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['rollMarked'] as const,
        decode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as DBLessonEnrolment['rollMarked']) === 'true',
        }),
      },
    }),
  ],
};

export const encodeLessonEnrolmentToDb = createEncodeToDbFn(LessonEnrolmentCodecConfig);
export const decodeLessonEnrolmentFromDb = createDecodeFromDbFn(LessonEnrolmentCodecConfig);
